import { offlineQueue, OfflineAction } from './offlineQueue';
import { apiService } from './api';
import { toast } from 'react-hot-toast';

export interface SyncStatus {
  isOnline: boolean;
  isSyncing: boolean;
  pendingActions: number;
  lastSyncTime: Date | null;
  syncErrors: SyncError[];
  syncHistory: SyncHistoryItem[];
}

export interface SyncError {
  id: string;
  actionId: string;
  actionType: string;
  error: string;
  timestamp: Date;
  retryCount: number;
}

export interface SyncHistoryItem {
  id: string;
  actionType: string;
  status: 'success' | 'failed' | 'retry';
  timestamp: Date;
  details: string;
}

class SyncManager {
  private static instance: SyncManager;
  private syncStatus: SyncStatus;
  private syncListeners: Array<(status: SyncStatus) => void> = [];
  private syncInterval: NodeJS.Timeout | null = null;
  private isProcessing = false;

  private constructor() {
    this.syncStatus = {
      isOnline: navigator.onLine,
      isSyncing: false,
      pendingActions: 0,
      lastSyncTime: null,
      syncErrors: [],
      syncHistory: []
    };

    this.initializeSync();
  }

  static getInstance(): SyncManager {
    if (!SyncManager.instance) {
      SyncManager.instance = new SyncManager();
    }
    return SyncManager.instance;
  }

  private async initializeSync() {
    // Listen for online/offline events
    window.addEventListener('online', this.handleOnline.bind(this));
    window.addEventListener('offline', this.handleOffline.bind(this));

    // Start periodic sync
    this.startPeriodicSync();

    // Initial sync check
    await this.checkSyncStatus();
  }

  private handleOnline() {
    console.log('[SYNC] Device came online');
    this.syncStatus.isOnline = true;
    this.notifyListeners();
    
    // Start syncing immediately
    this.processPendingActions();
  }

  private handleOffline() {
    console.log('[SYNC] Device went offline');
    this.syncStatus.isOnline = false;
    this.syncStatus.isSyncing = false;
    this.notifyListeners();
  }

  private startPeriodicSync() {
    // Check for pending actions every 30 seconds
    this.syncInterval = setInterval(async () => {
      if (this.syncStatus.isOnline && !this.isProcessing) {
        await this.processPendingActions();
      }
    }, 30000);
  }

  private async checkSyncStatus() {
    try {
      const queue = await offlineQueue.getQueue();
      const pending = queue.filter(action => 
        action.status === 'pending' || 
        (action.status === 'failed' && action.retryCount < 3)
      );
      
      this.syncStatus.pendingActions = pending.length;
      this.notifyListeners();
    } catch (error) {
      console.error('[SYNC] Error checking sync status:', error);
    }
  }

  async processPendingActions(): Promise<void> {
    if (this.isProcessing || !this.syncStatus.isOnline) {
      return;
    }

    this.isProcessing = true;
    this.syncStatus.isSyncing = true;
    this.notifyListeners();

    try {
      const queue = await offlineQueue.getQueue();
      const pendingActions = queue.filter(action => 
        action.status === 'pending' || 
        (action.status === 'failed' && action.retryCount < 3)
      );

      console.log(`[SYNC] Processing ${pendingActions.length} pending actions`);

      for (const action of pendingActions) {
        try {
          await this.processAction(action);
        } catch (error) {
          console.error(`[SYNC] Error processing action ${action.id}:`, error);
          await this.handleSyncError(action, error as Error);
        }
      }

      // Update sync status
      await this.checkSyncStatus();
      this.syncStatus.lastSyncTime = new Date();
      
      if (pendingActions.length > 0) {
        this.addSyncHistoryItem({
          actionType: 'BATCH_SYNC',
          status: 'success',
          details: `Synced ${pendingActions.length} actions`
        });
      }

    } catch (error) {
      console.error('[SYNC] Error processing pending actions:', error);
    } finally {
      this.isProcessing = false;
      this.syncStatus.isSyncing = false;
      this.notifyListeners();
    }
  }

  private async processAction(action: OfflineAction): Promise<void> {
    console.log(`[SYNC] Processing action: ${action.type}`, action.payload);

    try {
      let result: any;

      switch (action.type) {
        case 'MARK_PANEL_RECEIVED':
        case 'MARK_HARDWARE_RECEIVED':
          result = await this.processContentReceiving(action);
          break;
          
        case 'SCAN_CARTON':
          result = await this.processCartonScan(action);
          break;
          
        case 'RECEIVE_CARTON':
          result = await this.processCartonReceive(action);
          break;
          
        case 'UPDATE_CARTON_STATUS':
        case 'UPDATE_CARTON_RECEIVING_STATUS':
          result = await this.processCartonStatusUpdate(action);
          break;
          
        default:
          console.warn(`[SYNC] Unknown action type: ${action.type}`);
          return;
      }

      if (result && result.success) {
        // Remove from queue
        await offlineQueue.removeAction(action.id);
        
        this.addSyncHistoryItem({
          actionType: action.type,
          status: 'success',
          details: `Successfully synced ${action.type}`
        });

        // Show success toast
        toast.success('Action synced successfully');
      } else {
        throw new Error(result.error || 'Sync failed');
      }

    } catch (error) {
      throw error;
    }
  }

  private async processContentReceiving(action: OfflineAction): Promise<{ success: boolean; error?: string }> {
    const { cartonId, contentCode, projectId, isPanel, isHardware } = action.payload;
    
    try {
      // Check if we're online and API is available
      if (!navigator.onLine) {
        return { success: false, error: 'No internet connection' };
      }

      const response = await apiService.receiveCartonContent({
        cartonId,
        contentCode,
        projectId,
        isPanel,
        isHardware
      });
      
      return { success: response?.success || true };
    } catch (error) {
      console.error('[SYNC] Content receiving error:', error);
      const errorMessage = (error as any)?.response?.data?.message || (error as Error).message || 'Unknown error';
      return { success: false, error: errorMessage };
    }
  }

  private async processCartonScan(action: OfflineAction): Promise<{ success: boolean; error?: string }> {
    const { cartonId, scannedCode } = action.payload;
    
    try {
      if (!navigator.onLine) {
        return { success: false, error: 'No internet connection' };
      }

      const response = await apiService.scanCarton(cartonId, scannedCode);
      return { success: true };
    } catch (error) {
      console.error('[SYNC] Carton scan error:', error);
      const errorMessage = (error as any)?.response?.data?.message || (error as Error).message || 'Unknown error';
      return { success: false, error: errorMessage };
    }
  }

  private async processCartonReceive(action: OfflineAction): Promise<{ success: boolean; error?: string }> {
    const { cartonId, projectId, remarks } = action.payload;
    
    try {
      if (!navigator.onLine) {
        return { success: false, error: 'No internet connection' };
      }

      const response = await apiService.updateCartonStatus(cartonId, 'received', projectId, remarks);
      return { success: true };
    } catch (error) {
      console.error('[SYNC] Carton receive error:', error);
      const errorMessage = (error as any)?.response?.data?.message || (error as Error).message || 'Unknown error';
      return { success: false, error: errorMessage };
    }
  }

  private async processCartonStatusUpdate(action: OfflineAction): Promise<{ success: boolean; error?: string }> {
    const { cartonId, status, projectId, remarks } = action.payload;
    
    try {
      if (!navigator.onLine) {
        return { success: false, error: 'No internet connection' };
      }

      const response = await apiService.updateCartonStatus(cartonId, status, projectId, remarks);
      return { success: true };
    } catch (error) {
      console.error('[SYNC] Carton status update error:', error);
      const errorMessage = (error as any)?.response?.data?.message || (error as Error).message || 'Unknown error';
      return { success: false, error: errorMessage };
    }
  }

  private async handleSyncError(action: OfflineAction, error: Error) {
    const syncError: SyncError = {
      id: `error_${Date.now()}_${Math.random()}`,
      actionId: action.id,
      actionType: action.type,
      error: error.message,
      timestamp: new Date(),
      retryCount: action.retryCount || 0
    };

    this.syncStatus.syncErrors.push(syncError);
    
    // Update action retry count
    await offlineQueue.updateAction(action.id, {
      status: 'failed',
      retryCount: (action.retryCount || 0) + 1
    });

    this.addSyncHistoryItem({
      actionType: action.type,
      status: 'failed',
      details: `Failed to sync: ${error.message}`
    });

    // Show error toast
    toast.error(`Sync failed: ${error.message}`);
    
    this.notifyListeners();
  }

  private addSyncHistoryItem(item: Omit<SyncHistoryItem, 'id' | 'timestamp'>) {
    const historyItem: SyncHistoryItem = {
      id: `history_${Date.now()}_${Math.random()}`,
      timestamp: new Date(),
      ...item
    };

    this.syncStatus.syncHistory.unshift(historyItem);
    
    // Keep only last 100 history items
    if (this.syncStatus.syncHistory.length > 100) {
      this.syncStatus.syncHistory = this.syncStatus.syncHistory.slice(0, 100);
    }
  }

  // Public methods
  async forceSync(): Promise<void> {
    if (!this.syncStatus.isOnline) {
      toast.error('Cannot sync while offline');
      return;
    }

    await this.processPendingActions();
  }

  async clearSyncErrors(): Promise<void> {
    this.syncStatus.syncErrors = [];
    this.notifyListeners();
    toast.success('Sync errors cleared');
  }

  async clearSyncHistory(): Promise<void> {
    this.syncStatus.syncHistory = [];
    this.notifyListeners();
    toast.success('Sync history cleared');
  }

  getSyncStatus(): SyncStatus {
    return { ...this.syncStatus };
  }

  async getSyncStatusAsync(): Promise<SyncStatus> {
    return Promise.resolve({ ...this.syncStatus });
  }

  onSyncStatusChange(callback: (status: SyncStatus) => void): () => void {
    this.syncListeners.push(callback);
    return () => {
      this.syncListeners = this.syncListeners.filter(cb => cb !== callback);
    };
  }

  // Alias for consistency
  subscribe(callback: (status: SyncStatus) => void): () => void {
    return this.onSyncStatusChange(callback);
  }

  private notifyListeners() {
    this.syncListeners.forEach(callback => callback(this.syncStatus));
  }

  destroy() {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
    }
    
    window.removeEventListener('online', this.handleOnline.bind(this));
    window.removeEventListener('offline', this.handleOffline.bind(this));
  }
}

export const syncManager = SyncManager.getInstance();
